// The bell: pure helpers (tested). The server's inbox (GET /notifications/inbox) holds what happened to the signed-in person;
// the « new prospect » feed (GET /prospects/recent, only for those who can read prospects) is folded into the same list.

export const LEAD_EVENT = "NEW_PROSPECT";
export const PAGE_SIZE = 20;

// The server's items and the in-session new prospects, newest first. A new prospect has no server-side read state: it leaves the
// list when the person opens it or marks everything read.
export function leadItems(prospects) {
  return (prospects || []).map((p) => ({
    id: `lead:${p.id}`,
    event_type: LEAD_EVENT,
    title: "Nouveau prospect",
    summary: [p.pharmacy_name, p.commune || p.region].filter(Boolean).join(" · "),
    link: `/prospects/${p.id}`,
    created_at: p.created_at || new Date().toISOString(),
    read_at: null,
  }));
}

export function mergeItems(inboxItems, leads) {
  return [...(inboxItems || []), ...(leads || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)));
}

export function unreadTotal(inboxUnread, leads) {
  return (inboxUnread || 0) + (leads || []).length;
}

// Unread items present now that the previous reading did not know. The very first reading is a baseline: what was already
// waiting when the CRM opened is shown by the badge, not announced.
export function newArrivals(knownIds, items) {
  if (knownIds === null) return [];
  return items.filter((item) => !item.read_at && !knownIds.has(item.id));
}

// One toast for a batch: a single arrival says what it is, several are counted (ten imported prospects are one message).
export function arrivalMessage(items) {
  if (items.length === 1) return items[0].summary ? `${items[0].title} · ${items[0].summary}` : items[0].title;
  return `${items.length} nouvelles notifications`;
}

// Past three of the same kind within the same hour, the bell shows one line to open (« Tâche annulée (5) ») instead of five: an import or a
// reorganisation must not bury everything else. Returns entries in the order of their newest item; each is an item or a group.
export const GROUP_AFTER = 3;

export function groupItems(items, threshold = GROUP_AFTER) {
  const buckets = new Map();
  const order = [];
  for (const item of items || []) {
    const key = `${item.event_type}|${String(item.created_at).slice(0, 13)}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key).push(item);
  }
  return order.map((key) => {
    const list = buckets.get(key);
    if (list.length <= threshold) return list.map((item) => ({ type: "item", item }));
    return [{
      type: "group", id: `group:${key}`, event_type: list[0].event_type, title: list[0].title, items: list,
      unread: list.filter((i) => !i.read_at).length, created_at: list[0].created_at,
    }];
  }).flat();
}

export function badgeLabel(count) {
  return count > 9 ? "9+" : String(count);
}

const MINUTE = 60 * 1000;

// « à l'instant », « il y a 5 min », « il y a 3 h », « hier », then the date.
export function relativeTime(iso, now = Date.now()) {
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return "";
  const diff = Math.max(0, now - then);
  if (diff < MINUTE) return "à l'instant";
  if (diff < 60 * MINUTE) return `il y a ${Math.floor(diff / MINUTE)} min`;
  if (diff < 24 * 60 * MINUTE) return `il y a ${Math.floor(diff / (60 * MINUTE))} h`;
  if (diff < 48 * 60 * MINUTE) return "hier";
  return new Date(then).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

// A link the bell may follow: an internal path, never another site (a stored value is still data).
export function safeLink(link) {
  return typeof link === "string" && /^\/(?![/\\])[^\s\u0000-\u001f]*$/.test(link) ? link : null;
}
